/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Style } from '@opengeoweb/webmap';
import type {
  STACColormapEntry,
  STACItemAssetDefinitions,
  STACRenderEntry,
} from '../api';

export const STAC_ASSET_STYLE_PREFIX = 'asset:';

export const getStacAssetKeyFromStyle = (
  styleName?: string,
): string | undefined =>
  styleName?.startsWith(STAC_ASSET_STYLE_PREFIX)
    ? styleName.slice(STAC_ASSET_STYLE_PREFIX.length)
    : undefined;

export const isGeoTIFFAsset = (asset: {
  href?: string;
  type?: string;
}): boolean => {
  const type = asset.type?.toLowerCase() ?? '';
  const href = asset.href?.toLowerCase() ?? '';
  return type.includes('geotiff') || /\.tiff?(?:$|[?#])/.test(href);
};

export const isBrowserReadableGeoTIFFAsset = (asset: {
  href?: string;
  type?: string;
}): boolean => {
  if (!asset.href || !isGeoTIFFAsset(asset)) {
    return false;
  }

  const scheme = /^([a-z][a-z\d+.-]*):/i.exec(asset.href)?.[1].toLowerCase();
  return !scheme || scheme === 'http' || scheme === 'https';
};

/**
 * Converts a STAC render extension colormap to an OpenLayers WebGLTile palette style.
 *
 * The STAC colormap maps pixel values (0–255) to RGBA arrays where each channel is 0–255.
 * The returned object is suitable for use as the `style` option of `ol/layer/WebGLTile`.
 */
export const stacColormapToOlStyle = (
  colormap: Record<string, STACColormapEntry>,
): object => {
  const palette: string[] = Array.from({ length: 256 }, (_, i) => {
    const entry = colormap[String(i)];
    if (!entry) {
      return 'rgba(0,0,0,0)';
    }
    const [red, green, blue, alphaChannel] = entry;
    const alpha = (alphaChannel / 255).toFixed(4);
    return `rgba(${red},${green},${blue},${alpha})`;
  });

  return {
    color: ['palette', ['band', 1], palette],
  };
};

/**
 * Returns a grayscale WebGLTile style where band-1 value maps from black (0)
 * to white (255). Used as a safe default when no STAC render colormap exists.
 */
export const stacGrayscaleOlStyle = (): object => {
  const palette: string[] = Array.from(
    { length: 256 },
    (_, i) => `rgba(${i},${i},${i},1.0000)`,
  );

  return {
    color: ['palette', ['band', 1], palette],
  };
};

/**
 * Returns a grayscale style for normalized GeoTIFF samples where `['band', 1]`
 * is expected in [0, 1]. This avoids white-only output when raw data range is
 * not strictly 0-255.
 */
export const stacNormalizedGrayscaleOlStyle = (): object => {
  return {
    color: ['array', ['band', 1], ['band', 1], ['band', 1], 1],
  };
};

/**
 * Converts a STAC render extension `renders` map to an array of `Style` objects
 * compatible with the Layer Manager style selector.
 */
export const getRendersAsStyles = (
  renders: Record<string, STACRenderEntry>,
): Style[] =>
  Object.entries(renders).map(([name, entry]) => ({
    name,
    title: entry.title ?? name,
    legendURL: '',
    abstract: '',
  }));

export const getStacAssetStyles = (
  itemAssets?: STACItemAssetDefinitions,
): Style[] =>
  Object.entries(itemAssets ?? {})
    .filter(([, asset]) => isGeoTIFFAsset(asset))
    .sort(([firstKey, firstAsset], [secondKey, secondAsset]) => {
      const priority = (key: string, roles?: string[]): number => {
        if (roles?.includes('visual') || key === 'visual') {
          return 0;
        }
        if (key === 'data') {
          return 1;
        }
        return roles?.includes('data') ? 2 : 3;
      };
      return (
        priority(firstKey, firstAsset.roles) -
          priority(secondKey, secondAsset.roles) ||
        firstKey.localeCompare(secondKey)
      );
    })
    .map(([assetKey, asset]) => ({
      name: `${STAC_ASSET_STYLE_PREFIX}${assetKey}`,
      title: asset.title ?? assetKey,
      legendURL: '',
      abstract: '',
    }));

export const getStacCollectionStyles = (
  renders?: Record<string, STACRenderEntry>,
  itemAssets?: STACItemAssetDefinitions,
): Style[] => [
  ...(renders ? getRendersAsStyles(renders) : []),
  ...getStacAssetStyles(itemAssets),
];

export const resolveStacAssetKey = (
  assets: Record<string, { href?: string; type?: string; roles?: string[] }>,
  styleName?: string,
  renderAssets?: string[],
  configuredAssetKey?: string,
): string | undefined => {
  const candidates = [
    configuredAssetKey,
    getStacAssetKeyFromStyle(styleName),
    ...(renderAssets ?? []),
    Object.entries(assets).find(
      ([key, asset]) => key === 'visual' || asset.roles?.includes('visual'),
    )?.[0],
    assets.data ? 'data' : undefined,
    Object.entries(assets).find(([, asset]) =>
      asset.roles?.includes('data'),
    )?.[0],
    Object.entries(assets).find(([, asset]) =>
      isBrowserReadableGeoTIFFAsset(asset),
    )?.[0],
  ];

  return candidates.find((candidate): candidate is string =>
    Boolean(
      candidate &&
      assets[candidate] &&
      isBrowserReadableGeoTIFFAsset(assets[candidate]),
    ),
  );
};
